import { useState, useEffect, useMemo, useCallback, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { shallow } from "zustand/shallow";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@core/components/ui/DropdownMenu";
import { Button } from "@core/components/ui/Button";
import Loading from "@core/layouts/loading/Loading";
import { useAuthStore } from "@core/stores/authStore";
import { useUpdateMainICafe } from "@features/account/hooks/mutations/useUpdateMainICafe";
import { cn } from "@core/utils/domUtil";
import { Check, UserCog, ChevronDown } from "lucide-react";

function DefaultTrigger({ iCafeName }: { iCafeName: string }) {
  return (
    <div className="flex h-[50px] w-full cursor-pointer items-center gap-[6px] rounded-lg border border-gray-200 bg-gray-50 px-[20px]">
      <span className="line-clamp-1 w-full text-left">{iCafeName || "대표 PC방을 선택해주세요."}</span>
      <ChevronDown />
    </div>
  );
}

export default function ICafeList({ children, useAccountUserButton = false, className }: { children?: ReactNode; useAccountUserButton?: boolean; className?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { isLogin, auth, primaryICafeId, iCafeList } = useAuthStore(
    (state) => ({
      isLogin: state.isLogin,
      auth: state.auth,
      primaryICafeId: state.primaryICafeId,
      iCafeList: state.iCafeList,
    }),
    shallow,
  );
  const hasICafeList = useMemo(() => {
    return Array.isArray(iCafeList) && iCafeList.length > 0;
  }, [iCafeList]);
  const iCafeName = useMemo(() => {
    return iCafeList?.find((item) => item.iCafeId === primaryICafeId)?.companyFullName ?? "";
  }, [iCafeList, primaryICafeId]);
  const location = useLocation();

  const { mutate: updateMainICafe, isPending: isUpdateMainICafePending } = useUpdateMainICafe();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const clickHandler = useCallback(
    (iCafeIdParam?: string) => {
      if (iCafeIdParam === primaryICafeId || !iCafeIdParam) return;
      updateMainICafe(iCafeIdParam);
    },
    [updateMainICafe, primaryICafeId],
  );

  return isLogin && auth === "PLAYNC_USER_2" && hasICafeList ? (
    <div className={className}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className={cn("w-full")}>{children ?? <DefaultTrigger iCafeName={iCafeName} />}</DropdownMenuTrigger>
        <DropdownMenuContent className={cn("min-w-[194px]")} align="end">
          {iCafeList?.map((item) => (
            <DropdownMenuItem key={item.iCafeId} className="p-0!">
              <Button variant="text" size="large" className={cn("flex w-full items-center justify-between p-[8px]! text-left")} onClick={() => clickHandler(item.iCafeId)}>
                <span className="line-clamp-1 w-full">{item.companyFullName}</span>
                {item.isPrimary && <Check />}
              </Button>
            </DropdownMenuItem>
          ))}
          {useAccountUserButton && (
            <div className="mt-[9px] border-t-1 border-gray-100 pt-[9px]">
              <Button variant="borderBgGray50" size="small" className={cn("flex h-[36px] w-full items-center justify-center")} asChild>
                <Link to="/account/user">
                  <UserCog /> 내 정보 관리
                </Link>
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isUpdateMainICafePending && <Loading />}
    </div>
  ) : (
    <></>
  );
}
